import { configureStore, combineReducers, AnyAction } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import authReducer from '../reducers/authSlice';
import vehicleReducer from '../reducers/vehicleSlice';
import vehiclePhotosReducer from '../reducers/vehiclePhotosSlice';
import damageLocationsReducer from '../reducers/damageLocationsSlice';
import damagedPartsReducer from '../reducers/damagedPartsSlice';
import incidentDetailsReducer from '../reducers/incidentDetailsSlice';
import operatorInfoReducer from '../reducers/operatorInfoSlice';
import historyReducer from '../reducers/historySlice';
import navigationReducer from '../reducers/navigationSlice';
import { persistConfig } from './persistConfig';

// Combine all reducers
const appReducer = combineReducers({
  auth: authReducer,
  vehicle: vehicleReducer,
  vehiclePhotos: vehiclePhotosReducer,
  damageLocations: damageLocationsReducer,
  damagedParts: damagedPartsReducer,
  incidentDetails: incidentDetailsReducer,
  operatorInfo: operatorInfoReducer,
  history: historyReducer,
  navigation: navigationReducer
});

// Root reducer with reset functionality
const rootReducer = (state: any, action: AnyAction) => {
  if (action.type === 'RESET_STATE') {
    // Preserve auth state, reset everything else
    const { auth } = state;
    state = { auth };
  }
  if (action.type === 'PURGE_INCIDENT_DATA') {
    // Clear only incident-related data, preserve auth
    const { auth } = state;
    state = {
      auth,
      vehicle: undefined,
      vehiclePhotos: undefined,
      damageLocations: undefined,
      damagedParts: undefined,
      incidentDetails: undefined,
      operatorInfo: undefined
    };
  }
  return appReducer(state, action);
};

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore redux-persist actions
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          'auth/login/fulfilled',
          'incidentDetails/setIncidentDate'
        ],
        // Ignore these field paths in all actions
        ignoredActionPaths: ['payload.timestamp', 'meta.arg', 'register', 'rehydrate'],
        // Ignore these paths in the state
        ignoredPaths: ['incidentDetails.incidentDate', '_persist']
      }
    }),
  devTools: process.env.NODE_ENV !== 'production'
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Create persistor
export const persistor = persistStore(store);

// Export actions for resetting state
export const resetState = () => ({ type: 'RESET_STATE' });
export const purgeIncidentData = () => ({ type: 'PURGE_INCIDENT_DATA' });

export default store;